import type { ApplicationStatus } from '../fixtures/domain.js';
import { HIRING_STAGES, closingVariantFor, type HiringStage } from '../fixtures/rules.js';
import { Icon } from '../components/Icon.js';
import { ENDING_META, STAGE_META, badgeStyle } from './stageMeta.js';
import styles from './HiringStepper.module.css';

interface HiringStepperProps {
  status: ApplicationStatus;
  /** A write is in flight; every control is down until it lands. */
  busy?: boolean;
  onStage: (target: HiringStage) => void;
  onReject: () => void;
}

/**
 * EVERYTHING THIS CHAT CAN DO TO AN APPLICANT, ON ONE STRIP UNDER THE HEADER.
 *
 * The header used to carry this and could only ever offer one move: it has room
 * for one control, so it showed the next step forward and filed the rest behind
 * a "…". That answered "what can I press" and never "where am I", which is the
 * thing employers actually asked for — a step invisible until it is next is a
 * step nobody plans around.
 *
 * So the stages are drawn as what they are, an ordered run with all of it on
 * screen. Nothing of the pipeline is left in the header: the countdown, the
 * details toggle and, on an ended chat, the outcome, and that is all.
 *
 * THE ENDING IS ALWAYS THERE, AND ALWAYS PAST THE END OF THE RUN. It was behind
 * the overflow at every stage on the theory that a surface should not recommend
 * an ending — but rejecting is the move employers make most, and a first read
 * that comes to nothing is the common case, not the exception.
 *
 * Last, and behind a rule. The three to its left move somebody along a pipeline
 * they remain in; this one closes the conversation, and that is a difference in
 * kind rather than in degree. Reading order does the rest: an employer runs out
 * of forward moves before they reach it.
 *
 * Any step is reachable, backwards included. The pipeline is a near-one-way
 * state machine (`canAssignApplicationStatus`) but the one way it is not is
 * moving between the three stages, and an interview that turns out to be a
 * shortlist again is an ordinary thing to correct.
 *
 * A column of 480px or less draws `StageBar` instead.
 */
export function HiringStepper({ status, busy, onStage, onReject }: HiringStepperProps) {
  // Always the application's ending: this strip is only drawn on a chat that has
  // one. Asked rather than assumed so it cannot drift from the answer the rest
  // of the product gives.
  const ending = ENDING_META[closingVariantFor(true)];

  return (
    <div className={styles.bounds}>
      <div className={styles.strip}>
        <ol className={styles.steps} aria-label="Hiring stage">
          {HIRING_STAGES.map((stage) => {
            const meta = STAGE_META[stage];
            const isCurrent = stage === status;
            const state = isCurrent ? styles.current : styles.offer;
            return (
              <li key={stage}>
                <button
                  type="button"
                  className={`${styles.marker} ${state}`}
                  style={isCurrent ? badgeStyle(meta.token) : undefined}
                  /* The step it is already on is a fact, not a move. Said with
                     `aria-current` rather than only by colour, and unpressable
                     because pressing it would write the status it already holds. */
                  aria-current={isCurrent ? 'step' : undefined}
                  disabled={busy || isCurrent}
                  title={isCurrent ? `Marked as ${meta.past}` : `Mark as ${meta.past}`}
                  onClick={() => onStage(stage)}
                >
                  <Icon name={meta.icon} size={15} filled={isCurrent} />
                  <span className={styles.label}>{isCurrent ? meta.state : meta.name}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {/* The rule, and the whole of why the ending sits over here. Everything to
            its left moves an applicant along a pipeline they stay in; this closes
            the conversation. A gap alone said "different", a line says "different
            kind of thing", and it costs one pixel to stop a mis-aimed press from
            being the one that cannot be taken back. */}
        <span className={styles.divider} aria-hidden="true" />

        <button
          type="button"
          className={styles.reject}
          disabled={busy}
          title={ending.label}
          onClick={onReject}
        >
          <Icon name={ending.icon} size={15} />
          <span className={styles.label}>{ending.label}</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Stands in for the strip until the hiring status is read. Sized to the real
 * one so the conversation does not reflow when it swaps in — and drawn at all
 * because a strip that appeared late would shift the thread under the
 * employer's eye.
 */
export function HiringStepperSkeleton() {
  return (
    <div className={styles.bounds}>
      <div className={styles.strip}>
        <span className={styles.skeleton} aria-hidden="true" />
      </div>
    </div>
  );
}
